import { Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { connectState } from '@examdojo/angular/util';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { IconComponent } from '@examdojo/core/icon';
import { assertNonNullable } from '@examdojo/core/util/assert';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IonHeader, IonTitle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { EMPTY, finalize, tap } from 'rxjs';
import { AdminUserService } from '../admin-user.service';
import { AdminUserQuery } from '../admin-user.query';

@Component({
  selector: 'exam-remove-access-confirmation-dialog',
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar class="toolbar-with-closing-button !px-2">
        <div slot="start">
          <dojo-icon-button (click)="closeDialog()" [icon]="'times'"></dojo-icon-button>
        </div>
        <ion-title class="common-ion-title"> Remove Access </ion-title>
      </ion-toolbar>
    </ion-header>
    <div class="flex flex-col px-4 pb-4 pt-4">
      <div class="mb-6 flex items-center gap-3 rounded-[1rem] border border-red-300 bg-red-50 p-4 text-sm text-red-700">
        <dojo-icon [icon]="'triangle-exclamation'" [iconClass]="'text-red-500'"></dojo-icon>
        <span>Are you sure you want to downgrade this user to a student? This action cannot be undone.</span>
      </div>

      <div class="mt-6 flex w-full items-center justify-between">
        <dojo-button (click)="discard()" [pending]="isRemoving()" class="block" [fill]="'outline'">Cancel</dojo-button>
        <dojo-button
          (click)="confirm()"
          class="block"
          [fill]="'outline'"
          [customColor]="'red'"
          [pending]="isRemoving()"
        >
          Remove Access
        </dojo-button>
      </div>
    </div>
  `,
  imports: [ButtonComponent, IconButtonComponent, IonTitle, IonToolbar, IonHeader, IconComponent],
})
export class RemoveAccessConfirmationDialogComponent {
  constructor(
    private readonly modalController: ModalController,
    private readonly adminUserService: AdminUserService,
    private readonly destroyRef: DestroyRef,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly adminUserQuery: AdminUserQuery,
  ) {}

  readonly isRemoving = signal<boolean>(false);

  readonly state = connectState({});

  discard() {
    this.modalController.dismiss(false);
  }

  confirm() {
    this.isRemoving.set(true);

    const userId = this.adminUserQuery.getActiveId();
    assertNonNullable(userId, 'userId');

    this.adminUserService
      .updateUserRole(userId, 'STUDENT')
      .pipe(
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        tap(() => {
          this.modalController.dismiss(true);
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isRemoving.set(false);
        }),
      )
      .subscribe();
  }

  closeDialog() {
    this.modalController.dismiss();
  }
}
